/**
 * Formatting and data helpers shared by the stats page cards and the chart.
 */

const MONTHS_SHORT = {
    ru: ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'],
    en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
};

const SUFFIXES = {
    ru: ['', ' тыс.', ' млн', ' млрд', ' трлн'],
    en: ['', 'K', 'M', 'B', 'T'],
};

export const DAY_MS = 24 * 60 * 60 * 1000;

const lang = locale => (String(locale || 'en').startsWith('ru') ? 'ru' : 'en');

const trimZeros = str => str.replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');

/**
 * 5244038305 -> "5.24B" / "5.24 млрд", 1739 -> "1.74K" / "1.74 тыс."
 * @param  {Number} value
 * @param  {String} locale
 * @param  {Number} digits  significant fraction digits for the reduced number
 * @return {String}
 */
export const compactNumber = function compactNumber(value, locale = 'en', digits = 2) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return '—';
    }

    const abs = Math.abs(value);
    const sign = value < 0 ? '−' : '';
    const suffixes = SUFFIXES[lang(locale)];

    let tier = 0;
    while (tier < suffixes.length - 1 && abs >= 1000 ** (tier + 1)) {
        tier += 1;
    }

    if (tier === 0) {
        const rounded = abs >= 100 ? Math.round(abs) : Number(abs.toFixed(digits));
        return sign + trimZeros(String(rounded));
    }

    const reduced = abs / (1000 ** tier);
    // Three significant digits at most: 5.24, 52.4, 524
    let fraction = digits;
    if (reduced >= 100) {
        fraction = 0;
    } else if (reduced >= 10) {
        fraction = 1;
    }

    return sign + trimZeros(reduced.toFixed(fraction)) + suffixes[tier];
};

/**
 * Whole number with thin-space thousand groups: 3 247 046 265
 */
export const groupedNumber = function groupedNumber(value) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return '—';
    }
    return Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

/**
 * The digits of a price without the currency: 1.42, 0.000622, 3.95B
 */
const priceNumber = function priceNumber(abs, locale, compact, digits) {
    if (compact && abs >= 10000) {
        return compactNumber(abs, locale, digits);
    }

    if (abs >= 1000) {
        return groupedNumber(abs);
    }

    if (abs >= 1) {
        return abs.toFixed(Math.max(2, digits));
    }

    if (abs === 0) {
        return '0';
    }

    // Small prices keep four significant digits: 0.000622
    const decimals = Math.min(10, Math.max(2, 3 - Math.floor(Math.log10(abs)) + (digits - 2)));
    return trimZeros(abs.toFixed(decimals));
};

/**
 * Dollar amounts: $1.42, $0.000622, $3.95B
 */
export const formatUsd = function formatUsd(value, locale = 'en', compact = false, digits = 2) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return '—';
    }

    const abs = Math.abs(value);
    const sign = value < 0 ? '−' : '';

    return sign + '$' + priceNumber(abs, locale, compact, digits);
};

const CURRENCY_SYMBOLS = {
    USD: '$',
    EUR: '€',
    RUB: '₽',
    AED: 'AED',
};

/**
 * Price in the given currency, the dollar in front and the others after the
 * number: $1.42, 112.40 ₽, 5.21 AED
 */
export const formatFiat = function formatFiat(value, locale = 'en', currency = 'USD', digits = 2) {
    const code = String(currency || 'USD').toUpperCase();

    if (code === 'USD') {
        return formatUsd(value, locale, false, digits);
    }

    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return '—';
    }

    const abs = Math.abs(value);
    const sign = value < 0 ? '−' : '';

    return `${sign}${priceNumber(abs, locale, false, digits)} ${CURRENCY_SYMBOLS[code] || code}`;
};

/**
 * Signed percent from a ratio: 0.0418 -> "+4.18%"
 */
export const formatPercent = function formatPercent(ratio, digits = 2) {
    if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) {
        return '—';
    }
    let sign = '';
    if (ratio > 0) {
        sign = '+';
    } else if (ratio < 0) {
        sign = '−';
    }
    return sign + Math.abs(ratio * 100).toFixed(digits) + '%';
};

/**
 * Relative change between the first and the last value: (last - first) / first
 */
export const relativeChange = function relativeChange(first, last) {
    if (!first || !Number.isFinite(first) || !Number.isFinite(last)) {
        return 0;
    }
    return (last - first) / Math.abs(first);
};

/**
 * Date label for the time axis.
 * @param  {Number} ms
 * @param  {String} mode   hour | day | month | year
 * @param  {String} locale
 * @return {String}
 */
export const formatAxisDate = function formatAxisDate(ms, mode, locale = 'en') {
    const date = new Date(ms);
    const months = MONTHS_SHORT[lang(locale)];
    const month = months[date.getMonth()];

    switch (mode) {
        case 'hour':
            return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
        case 'day':
            return lang(locale) === 'ru'
                ? `${date.getDate()} ${month}`
                : `${month} ${date.getDate()}`;
        case 'month':
            return date.getMonth() === 0
                ? `${month} ’${String(date.getFullYear()).slice(-2)}`
                : month;
        case 'year':
            return String(date.getFullYear());
        default:
            return date.toLocaleDateString(locale);
    }
};

/**
 * Full date for tooltips: "6 сентября 2026, 14:30" / "September 6, 2026, 14:30"
 */
export const formatTooltipDate = function formatTooltipDate(ms, withTime, locale = 'en') {
    const date = new Date(ms);
    const day = date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' }).replace(' г.', '');

    if (!withTime) {
        return day;
    }

    const time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    return `${day}, ${time}`;
};

/**
 * Short date with time for the validation card: "6 сен, 14:31" / "Sep 6, 14:31"
 */
export const formatShortDateTime = function formatShortDateTime(ms, locale = 'en') {
    const date = new Date(ms);
    const day = formatAxisDate(ms, 'day', locale);
    const time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    const year = date.getFullYear() === new Date().getFullYear() ? '' : ` ${date.getFullYear()}`;
    return `${day}${year}, ${time}`;
};

/**
 * Remaining time: "8ч 12м" / "8h 12m"
 */
export const formatDuration = function formatDuration(ms, locale = 'en') {
    const total = Math.max(0, Math.round(ms / 1000));
    const days = Math.floor(total / 86400);
    const hours = Math.floor((total % 86400) / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const units = lang(locale) === 'ru' ? ['д', 'ч', 'м'] : ['d', 'h', 'm'];

    const parts = [];
    if (days) parts.push(days + units[0]);
    if (hours || days) parts.push(hours + units[1]);
    parts.push(minutes + units[2]);

    return parts.join(' ');
};

/**
 * Largest-Triangle-Three-Buckets downsampling. Keeps the peaks a plain
 * bucket average would flatten, so a 3000-point price line still shows
 * every spike at 400 points.
 * @param  {Array<{t: Number, v: Number}>} data
 * @param  {Number} threshold
 * @return {Array}
 */
export const downsample = function downsample(data, threshold) {
    const length = data.length;
    if (threshold >= length || threshold < 3) {
        return data;
    }

    const sampled = [data[0]];
    const bucketSize = (length - 2) / (threshold - 2);
    let a = 0;

    for (let i = 0; i < threshold - 2; i += 1) {
        const rangeStart = Math.floor((i + 1) * bucketSize) + 1;
        const rangeEnd = Math.min(Math.floor((i + 2) * bucketSize) + 1, length);

        let avgT = 0;
        let avgV = 0;
        for (let j = rangeStart; j < rangeEnd; j += 1) {
            avgT += data[j].t;
            avgV += data[j].v;
        }
        const count = rangeEnd - rangeStart || 1;
        avgT /= count;
        avgV /= count;

        const bucketStart = Math.floor(i * bucketSize) + 1;
        const bucketEnd = Math.min(Math.floor((i + 1) * bucketSize) + 1, length);

        let maxArea = -1;
        let next = bucketStart;
        for (let j = bucketStart; j < bucketEnd; j += 1) {
            const area = Math.abs((data[a].t - avgT) * (data[j].v - data[a].v) - (data[a].t - data[j].t) * (avgV - data[a].v));
            if (area > maxArea) {
                maxArea = area;
                next = j;
            }
        }

        sampled.push(data[next]);
        a = next;
    }

    sampled.push(data[length - 1]);
    return sampled;
};

/**
 * Keeps the points of the last `days` days (all of them for `days` = 0).
 */
export const sliceDays = function sliceDays(points, days) {
    if (!days || !points.length) {
        return points;
    }
    const from = Date.now() - days * DAY_MS;
    const idx = points.findIndex(p => p.t >= from);
    // At least two points, so a change can still be computed:
    return idx <= 0 ? points : points.slice(Math.min(idx, points.length - 2));
};

/**
 * Value of the point closest to (but not after) the given time.
 */
export const valueAt = function valueAt(points, ms) {
    let result;
    for (let i = 0; i < points.length; i += 1) {
        if (points[i].t > ms) break;
        result = points[i].v;
    }
    return result;
};

/**
 * Clock time for live tables: "14:31:07" today, "6 сен, 14:31:07" on another day.
 */
export const formatClock = function formatClock(ms, locale = 'en') {
    const date = new Date(ms);
    const now = new Date();
    const time = [date.getHours(), date.getMinutes(), date.getSeconds()]
        .map(part => String(part).padStart(2, '0'))
        .join(':');

    const sameDay = date.getFullYear() === now.getFullYear()
        && date.getMonth() === now.getMonth()
        && date.getDate() === now.getDate();

    if (sameDay) {
        return time;
    }

    const year = date.getFullYear() === now.getFullYear() ? '' : ` ${date.getFullYear()}`;
    return `${formatAxisDate(ms, 'day', locale)}${year}, ${time}`;
};
